import { AvatarDisc, bandColors, colors, radii, strokes, Text } from '@kk/ui';
import type { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';

import { avatarUrls } from '@/assets';
import { placeholders } from '@/copy/placeholders';
import { ageLabel } from '@/lib/age';
import type { OrgChildRef, ScoreBand } from '@/types/wire';

const BANDS: readonly ScoreBand[] = ['Building', 'Growing', 'Good', 'Great', 'Super'];

/** O02 SCORE pill: the band's colour fill, 2px ink border, pill radius — never a number. */
export function BandPill({ band, label }: { band: ScoreBand; label?: string }) {
  const index = Math.max(0, BANDS.indexOf(band));
  return (
    <View
      style={[styles.bandPill, { backgroundColor: bandColors[index] ?? bandColors[0] }]}
      testID="band-pill"
    >
      <Text variant="chipSelected" tone="ink">
        {label ?? band}
      </Text>
    </View>
  );
}

/** Sidebar count pill: deep teal (Approvals) or magenta (Redemptions). Renders nothing at 0. */
export function CountPill({ count, tone }: { count: number; tone: 'teal' | 'magenta' }) {
  if (count <= 0) return null;
  return (
    <View
      style={[
        styles.countPill,
        { backgroundColor: tone === 'teal' ? colors.deepTeal : colors.magenta },
      ]}
    >
      <Text variant="badge" tone="white" style={styles.countText}>
        {String(count)}
      </Text>
    </View>
  );
}

/** Cub head on its pastel disc from the copied mobile assets. */
export function Avatar({ child, size = 32 }: { child: OrgChildRef; size?: number }) {
  return (
    <AvatarDisc
      disc={child.avatar}
      source={{ uri: avatarUrls[child.avatar] }}
      size={size}
      accessibilityLabel={placeholders.redemptions.avatarAlt(child.firstName)}
    />
  );
}

/**
 * O01/O02 CHILD cell: avatar, first name and (O01, phone cards) the age label — nothing else about the child.
 * `showAge={false}` where the age band already has its own column (O02 desktop).
 */
export function ChildCell({
  child,
  size = 32,
  showAge = true,
}: {
  child: OrgChildRef;
  size?: number;
  showAge?: boolean;
}) {
  return (
    <div className="child-cell">
      <Avatar child={child} size={size} />
      <div className="cell">
        <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
          {child.firstName}
        </Text>
        {showAge ? (
          <Text variant="meta" tone="muted">
            {ageLabel(child)}
          </Text>
        ) : null}
      </div>
    </div>
  );
}

/** Deep-teal 12/700 inline link ("Note ▸", "Show", "Open", "Export CSV"): a 44px-tall DOM button. */
export function LinkButton({
  label,
  onPress,
  disabled,
  testID,
}: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  testID?: string;
}) {
  return (
    <button
      type="button"
      className="link-button"
      onClick={onPress}
      disabled={disabled}
      data-testid={testID}
    >
      <Text variant="labelStrong" tone="link">
        {label}
      </Text>
    </button>
  );
}

/** Table column head (11–12px caps, muted). */
export function ColumnHead({ children }: { children: string }) {
  return (
    <Text variant="sectionTitle" tone="muted">
      {children}
    </Text>
  );
}

/** Loading / empty / error line under a header, in the deck's muted meta size. */
export function Status({ children, testID }: { children: ReactNode; testID?: string }) {
  return (
    <Text variant="bodySm" tone="muted" testID={testID}>
      {children}
    </Text>
  );
}

const styles = StyleSheet.create({
  bandPill: {
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    borderWidth: strokes.playful,
    borderColor: colors.ink,
    paddingHorizontal: 10,
    paddingVertical: 1,
  },
  countPill: {
    minWidth: 20,
    height: 20,
    borderRadius: radii.pill,
    paddingHorizontal: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countText: { letterSpacing: 0 },
});
